import React, { useState, useEffect } from 'react';
import { X, PartyPopper, Cake, Gift, Sparkles, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import confetti from 'canvas-confetti';

const BirthdayAnimation = ({ userName, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Trigger confetti
    const duration = 3 * 1000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function() {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);
      
      // Left side
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#1B2D4F', '#E86C3D', '#FFD93D', '#FF6B6B', '#00D4AA']
      });
      
      // Right side
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#1B2D4F', '#E86C3D', '#FFD93D', '#FF6B6B', '#00D4AA']
      });
    }, 250);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-gradient-to-br from-primary via-primary to-secondary rounded-3xl p-8 max-w-md w-full text-center overflow-hidden animate-bounce-in">
        {/* Decorative elements */}
        <div className="absolute top-4 left-4 animate-float">
          <PartyPopper className="w-8 h-8 text-yellow-300" />
        </div>
        <div className="absolute top-4 right-4 animate-float-delayed">
          <Gift className="w-8 h-8 text-pink-300" />
        </div>
        <div className="absolute bottom-4 left-8 animate-pulse">
          <Sparkles className="w-6 h-6 text-yellow-200" />
        </div>
        <div className="absolute bottom-8 right-8 animate-pulse">
          <Star className="w-6 h-6 text-orange-200" fill="currentColor" />
        </div>
        
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-14 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        
        {/* Content */}
        <div className="relative">
          <div className="w-24 h-24 mx-auto mb-6 relative">
            <div className="absolute inset-0 bg-white/20 rounded-full animate-ping" />
            <div className="relative w-full h-full bg-white rounded-full flex items-center justify-center">
              <Cake className="w-12 h-12 text-primary" />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Happy Birthday!
          </h1>
          
          <p className="text-white/90 text-xl mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {userName}! 🎉
          </p>
          
          <p className="text-white/80 text-sm mb-6">
            Wishing you an amazing day filled with joy and celebration! Keep shining and crushing your goals!
          </p>
          
          <Button 
            onClick={handleClose}
            className="bg-white text-primary hover:bg-white/90 font-bold px-8"
          >
            Thank You! 🎈
          </Button>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes bounce-in {
          0% { transform: scale(0.5); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(-10deg); }
          50% { transform: translateY(-10px) rotate(10deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0) rotate(10deg); }
          50% { transform: translateY(-10px) rotate(-10deg); }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
        .animate-float {
          animation: float 2s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 2s ease-in-out infinite 0.5s;
        }
      `}</style>
    </div>
  );
};

// Hook to check for birthday
export const useBirthdayCheck = () => {
  const [isBirthday, setIsBirthday] = useState(false);
  const [userName, setUserName] = useState('');
  const [hasShown, setHasShown] = useState(false);

  useEffect(() => {
    const checkBirthday = async () => {
      // Check if already shown today
      const lastShown = localStorage.getItem('birthday_shown_date');
      const today = new Date().toISOString().split('T')[0];
      if (lastShown === today) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/birthday-check`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.ok) {
          const data = await res.json();
          if (data.is_birthday && data.name) {
            setIsBirthday(true);
            setUserName(data.name);
          }
        }
      } catch (error) {
        console.error('Birthday check failed:', error);
      }
    };

    checkBirthday();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismissBirthday = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('birthday_shown_date', today);
    setIsBirthday(false);
    setHasShown(true);
  };

  return { isBirthday: isBirthday && !hasShown, userName, dismissBirthday };
};

export default BirthdayAnimation;
